// API Keys Tab Component for AdminPage
import React, { useState } from 'react';
import { Plus, Key, Eye, EyeOff, Copy, Trash2, ExternalLink, AlertCircle, FileText, Code, Check, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { apiKeySuggestions } from './constants';
import * as api from '../../services/api';

// CRM API Documentation for instructions modal
const CRM_API_ENDPOINTS = [
  {
    category: 'Empresas',
    endpoints: [
      { method: 'GET', path: '/api/empresas', description: 'Listar todas las empresas', params: 'search, stage, category, assigned_to' },
      { method: 'GET', path: '/api/empresas/{id}', description: 'Obtener una empresa específica' },
      { method: 'POST', path: '/api/empresas', description: 'Crear nueva empresa', body: '{ name, email, phone, website, stage, category, assigned_to }' },
      { method: 'PUT', path: '/api/empresas/{id}', description: 'Actualizar empresa', body: '{ name, email, phone, website, stage, category, assigned_to }' },
      { method: 'DELETE', path: '/api/empresas/{id}', description: 'Eliminar empresa' },
    ]
  },
  {
    category: 'Contactos',
    endpoints: [
      { method: 'GET', path: '/api/contacts', description: 'Listar todos los contactos' },
      { method: 'POST', path: '/api/empresas/{empresa_id}/contacts', description: 'Agregar contacto a empresa', body: '{ name, email, phone, position, is_primary }' },
      { method: 'PUT', path: '/api/contacts/{id}', description: 'Actualizar contacto', body: '{ name, email, phone, position }' },
      { method: 'DELETE', path: '/api/contacts/{id}', description: 'Eliminar contacto' },
    ]
  },
  {
    category: 'Notas',
    endpoints: [
      { method: 'POST', path: '/api/empresas/{empresa_id}/notes', description: 'Agregar nota a empresa', body: '{ text }' },
      { method: 'DELETE', path: '/api/notes/{id}', description: 'Eliminar nota' },
    ]
  },
  {
    category: 'Tareas',
    endpoints: [
      { method: 'GET', path: '/api/tasks', description: 'Listar tareas', params: 'status, assigned_to, empresa_id' },
      { method: 'POST', path: '/api/tasks', description: 'Crear tarea', body: '{ title, description, due_date, priority, status, assigned_to, empresa_id, contact_id }' },
      { method: 'PUT', path: '/api/tasks/{id}', description: 'Actualizar tarea', body: '{ title, description, due_date, priority, status }' },
      { method: 'DELETE', path: '/api/tasks/{id}', description: 'Eliminar tarea' },
    ]
  },
  {
    category: 'Embudos (Deals)',
    endpoints: [
      { method: 'GET', path: '/api/pipelines', description: 'Listar pipelines' },
      { method: 'GET', path: '/api/deals', description: 'Listar deals', params: 'pipeline_id, empresa_id' },
      { method: 'POST', path: '/api/deals', description: 'Crear deal', body: '{ pipeline_id, stage_id, empresa_id, value, monthly_value }' },
      { method: 'PUT', path: '/api/deals/{id}', description: 'Actualizar deal', body: '{ stage_id, value, monthly_value }' },
      { method: 'DELETE', path: '/api/deals/{id}', description: 'Eliminar deal' },
    ]
  },
  {
    category: 'Email (Gmail)',
    endpoints: [
      { method: 'POST', path: '/api/google/gmail/send', description: 'Enviar email con Gmail conectado', body: '{ to, subject, body, cc, bcc, empresa_id }' },
      { method: 'GET', path: '/api/google/gmail/messages', description: 'Listar emails', params: 'max_results, query' },
    ]
  },
  {
    category: 'Agentes AI Personalizados',
    endpoints: [
      { method: 'GET', path: '/api/agents', description: 'Listar todos tus agentes AI configurados' },
      { method: 'GET', path: '/api/agents/{agent_id}', description: 'Obtener detalles de un agente específico' },
      { method: 'POST', path: '/api/agents/{agent_id}/chat', description: 'Enviar mensaje al agente y recibir respuesta', body: '{ message, empresa_id, contact_id, session_id }' },
      { method: 'POST', path: '/api/agents/{agent_id}/execute', description: 'Ejecutar instrucción con el agente', body: '{ instruction, empresa_id, execute_immediately }' },
      { method: 'GET', path: '/api/agents/{agent_id}/actions', description: 'Obtener acciones pendientes del agente', params: 'status, limit' },
      { method: 'POST', path: '/api/agents/{agent_id}/actions/{action_id}/approve', description: 'Aprobar una acción pendiente' },
      { method: 'POST', path: '/api/agents/{agent_id}/actions/{action_id}/reject', description: 'Rechazar una acción pendiente', body: '{ reason }' },
    ]
  },
  {
    category: 'Integración n8n',
    endpoints: [
      { method: 'GET', path: '/api/agents/n8n/pending-actions', description: 'Obtener acciones pendientes (para webhooks n8n)' },
      { method: 'POST', path: '/api/agents/n8n/command', description: 'Responder a una acción', body: '{ command, action_id, approved, feedback }' },
      { method: 'POST', path: '/api/agents/n8n/execute-instruction', description: 'Ejecutar instrucción directa', body: '{ instruction, empresa_id, agent_id }' },
      { method: 'POST', path: '/api/n8n/webhook', description: 'Webhook bidireccional de n8n', body: '{ event, data, action_id }' },
    ]
  },
];

const ApiKeysTab = ({ apiKeys, setApiKeys }) => {
  const [showAddKeyModal, setShowAddKeyModal] = useState(false);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [generatedKey, setGeneratedKey] = useState(null);
  const [newApiKey, setNewApiKey] = useState({ service_name: '', api_key: '', is_active: true, custom_name: '' });
  const [showApiKey, setShowApiKey] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [copiedText, setCopiedText] = useState(null);

  const handleAddApiKey = async () => {
    try {
      let keyData = { ...newApiKey };
      
      if (newApiKey.service_name === 'other') {
        if (!newApiKey.custom_name.trim()) {
          alert('Por favor ingresa un nombre para el servicio');
          return;
        }
        keyData.service_name = newApiKey.custom_name.trim().toLowerCase().replace(/\s+/g, '_');
        keyData.display_name = newApiKey.custom_name.trim();
      }
      
      if (newApiKey.service_name === 'crm_api') {
        if (!newApiKey.custom_name.trim()) {
          alert('Por favor ingresa un nombre para la conexión');
          return;
        }
        const generatedKeyValue = 'crm_' + crypto.randomUUID().replace(/-/g, '');
        keyData.api_key = generatedKeyValue;
        keyData.display_name = newApiKey.custom_name.trim();
        keyData.is_crm_key = true;
        
        await api.createAPIKey(keyData);
        const keysRes = await api.getAPIKeys();
        setApiKeys(keysRes.data);
        setShowAddKeyModal(false);
        setNewApiKey({ service_name: '', api_key: '', is_active: true, custom_name: '' });
        
        // Show instructions modal with the generated key
        setGeneratedKey({ key: generatedKeyValue, name: keyData.display_name });
        setShowInstructionsModal(true);
        return;
      }
      
      await api.createAPIKey(keyData);
      const keysRes = await api.getAPIKeys();
      setApiKeys(keysRes.data);
      setShowAddKeyModal(false);
      setNewApiKey({ service_name: '', api_key: '', is_active: true, custom_name: '' });
    } catch (error) {
      console.error('Error creating API key:', error);
      alert('Error al crear API key');
    }
  };

  const handleDeleteApiKey = async (id) => {
    if (window.confirm('¿Está seguro de eliminar esta API key?')) {
      try {
        await api.deleteAPIKey(id);
        setApiKeys(apiKeys.filter(k => k.id !== id));
      } catch (error) {
        console.error('Error deleting API key:', error);
      }
    }
  };

  const copyToClipboard = (text, label = null) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label || text);
    setTimeout(() => setCopiedText(null), 2000);
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const getBaseUrl = () => {
    return window.location.origin;
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold text-gray-700">API Keys</CardTitle>
            <p className="text-sm text-gray-500 mt-1">Gestiona las claves de integración con servicios externos</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => {
                setNewApiKey({ service_name: 'crm_api', api_key: '', is_active: true, custom_name: '' });
                setShowAddKeyModal(true);
              }}
            >
              <Key size={18} className="mr-2" />
              Generar API Key CRM
            </Button>
            <Button 
              onClick={() => {
                setNewApiKey({ service_name: 'other', api_key: '', is_active: true, custom_name: '' });
                setShowAddKeyModal(true);
              }} 
              className="bg-[#D4A843] hover:bg-[#c49a3a]"
              data-testid="add-api-key-btn"
            >
              <Plus size={18} className="mr-2" />
              Agregar Clave
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {apiKeys.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Claves Configuradas</h4>
              <div className="space-y-2">
                {apiKeys.map((key) => (
                  <div key={key.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Key size={18} className={key.is_active ? 'text-green-500' : 'text-gray-400'} />
                      <div>
                        <p className="font-medium text-gray-800">{key.display_name || key.service_name}</p>
                        {key.display_name && key.display_name !== key.service_name && (
                          <p className="text-xs text-gray-400">{key.service_name}</p>
                        )}
                        <div className="flex items-center space-x-2 mt-1">
                          <code className="text-xs bg-gray-200 px-2 py-1 rounded">
                            {showApiKey[key.id] ? key.api_key : `***${key.api_key?.slice(-8) || ''}`}
                          </code>
                          <button onClick={() => setShowApiKey({ ...showApiKey, [key.id]: !showApiKey[key.id] })} className="p-1 hover:bg-gray-200 rounded">
                            {showApiKey[key.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                          <button onClick={() => copyToClipboard(key.api_key, `key-${key.id}`)} className="p-1 hover:bg-gray-200 rounded">
                            {copiedText === `key-${key.id}` ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {key.is_crm_key && (
                        <button 
                          onClick={() => {
                            setGeneratedKey({ key: key.api_key, name: key.display_name || key.service_name });
                            setShowInstructionsModal(true);
                          }}
                          className="p-2 hover:bg-blue-50 rounded-lg text-blue-500"
                          title="Ver instrucciones de uso"
                        >
                          <FileText size={16} />
                        </button>
                      )}
                      <Badge className={key.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {key.is_active ? 'Activa' : 'Inactiva'}
                      </Badge>
                      <button onClick={() => handleDeleteApiKey(key.id)} className="p-2 hover:bg-red-50 rounded-lg">
                        <Trash2 size={16} className="text-red-500" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <h4 className="font-medium text-gray-700 mb-4">Servicios Disponibles</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {apiKeySuggestions.map((suggestion) => (
                <div key={suggestion.key} className="p-4 border rounded-lg hover:border-[#D4A843] transition-colors">
                  <div className="flex items-start justify-between mb-2">
                    <h5 className="font-medium text-gray-800">{suggestion.name}</h5>
                    <a href={suggestion.url} target="_blank" rel="noopener noreferrer" className="text-[#D4A843] hover:underline">
                      <ExternalLink size={14} />
                    </a>
                  </div>
                  <p className="text-sm text-gray-500 mb-3">{suggestion.description}</p>
                  <div className="p-2 bg-blue-50 rounded text-xs text-blue-700 mb-3">
                    <AlertCircle size={12} className="inline mr-1" />
                    {suggestion.instructions}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full"
                    onClick={() => {
                      setNewApiKey({ ...newApiKey, service_name: suggestion.key });
                      setShowAddKeyModal(true);
                    }}
                  >
                    Configurar
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add API Key Modal */}
      <Dialog open={showAddKeyModal} onOpenChange={setShowAddKeyModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {newApiKey.service_name === 'crm_api' ? 'Generar API Key del CRM' : 
               newApiKey.service_name === 'other' ? 'Agregar Clave Personalizada' :
               `Configurar ${apiKeySuggestions.find(s => s.key === newApiKey.service_name)?.name || 'API Key'}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {(newApiKey.service_name === 'other' || newApiKey.service_name === 'crm_api') && (
              <div>
                <label className="text-sm font-medium text-gray-700">
                  {newApiKey.service_name === 'crm_api' ? 'Nombre de la Conexión' : 'Nombre del Servicio'}
                </label>
                <Input 
                  value={newApiKey.custom_name} 
                  onChange={(e) => setNewApiKey({...newApiKey, custom_name: e.target.value})} 
                  placeholder={newApiKey.service_name === 'crm_api' ? 'Ej: n8n Workflow' : 'Ej: Mi Servicio'} 
                />
              </div>
            )}
            {newApiKey.service_name !== 'crm_api' && (
              <div>
                <label className="text-sm font-medium text-gray-700">API Key</label>
                <Input 
                  type="password"
                  value={newApiKey.api_key} 
                  onChange={(e) => setNewApiKey({...newApiKey, api_key: e.target.value})} 
                  placeholder="Pega tu API key aquí" 
                />
              </div>
            )}
            {newApiKey.service_name === 'crm_api' && (
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <p>Se generará automáticamente una API key única que podrás usar para conectar servicios externos como n8n.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddKeyModal(false)}>Cancelar</Button>
            <Button onClick={handleAddApiKey} className="bg-[#D4A843] hover:bg-[#c49a3a]">
              {newApiKey.service_name === 'crm_api' ? 'Generar' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CRM API Instructions Modal */}
      <Dialog open={showInstructionsModal} onOpenChange={setShowInstructionsModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="text-green-500" size={24} />
              API Key Generada Exitosamente
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            {/* Generated Key Display */}
            {generatedKey && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700 mb-2 font-medium">Tu nueva API Key para &quot;{generatedKey.name}&quot;:</p>
                <div className="flex items-center gap-2 bg-white p-2 rounded border">
                  <code className="flex-1 text-sm font-mono break-all">{generatedKey.key}</code>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => copyToClipboard(generatedKey.key, 'api-key')}
                    className="shrink-0"
                  >
                    {copiedText === 'api-key' ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                  </Button>
                </div>
                <p className="text-xs text-orange-600 mt-2">⚠️ Guarda esta clave ahora. No se mostrará completa nuevamente.</p>
              </div>
            )}

            {/* Instructions Tabs */}
            <Tabs defaultValue="quick" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="quick">Inicio Rápido</TabsTrigger>
                <TabsTrigger value="n8n-agents">n8n + Agentes</TabsTrigger>
                <TabsTrigger value="endpoints">Endpoints</TabsTrigger>
                <TabsTrigger value="examples">Ejemplos</TabsTrigger>
              </TabsList>

              {/* Quick Start Tab */}
              <TabsContent value="quick" className="space-y-4 mt-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-800 mb-3">Cómo usar la API</h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-start gap-3">
                      <span className="bg-[#D4A843] text-white w-6 h-6 rounded-full flex items-center justify-center text-xs shrink-0">1</span>
                      <div>
                        <p className="font-medium">Base URL</p>
                        <div className="flex items-center gap-2 mt-1">
                          <code className="bg-gray-200 px-2 py-1 rounded text-xs">{getBaseUrl()}/api</code>
                          <button onClick={() => copyToClipboard(`${getBaseUrl()}/api`, 'base-url')} className="p-1 hover:bg-gray-200 rounded">
                            {copiedText === 'base-url' ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="bg-[#D4A843] text-white w-6 h-6 rounded-full flex items-center justify-center text-xs shrink-0">2</span>
                      <div>
                        <p className="font-medium">Header de Autenticación</p>
                        <p className="text-gray-600 text-xs mt-1">Incluye uno de estos headers en cada petición:</p>
                        <div className="space-y-1 mt-2">
                          <code className="bg-gray-200 px-2 py-1 rounded text-xs block">Authorization: Bearer {generatedKey?.key || 'TU_API_KEY'}</code>
                          <span className="text-xs text-gray-500">o</span>
                          <code className="bg-gray-200 px-2 py-1 rounded text-xs block">X-API-Key: {generatedKey?.key || 'TU_API_KEY'}</code>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="bg-[#D4A843] text-white w-6 h-6 rounded-full flex items-center justify-center text-xs shrink-0">3</span>
                      <div>
                        <p className="font-medium">Content-Type</p>
                        <code className="bg-gray-200 px-2 py-1 rounded text-xs">Content-Type: application/json</code>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                    <AlertCircle size={16} />
                    Configuración Básica en n8n
                  </h4>
                  <ol className="text-sm text-blue-700 space-y-2 list-decimal list-inside">
                    <li>En n8n, agrega un nodo <strong>HTTP Request</strong></li>
                    <li>Configura el método (GET, POST, PUT, DELETE)</li>
                    <li>URL: <code className="bg-blue-100 px-1 rounded">{getBaseUrl()}/api/[endpoint]</code></li>
                    <li>En <strong>Authentication</strong>, selecciona &quot;Header Auth&quot;</li>
                    <li>Name: <code className="bg-blue-100 px-1 rounded">X-API-Key</code></li>
                    <li>Value: Pega tu API Key</li>
                  </ol>
                </div>
              </TabsContent>

              {/* n8n + Custom Agents Tab */}
              <TabsContent value="n8n-agents" className="space-y-4 mt-4">
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <h4 className="font-medium text-purple-800 mb-3">Usar tu Agente AI Personalizado en n8n</h4>
                  <p className="text-sm text-purple-700 mb-3">
                    En lugar de usar el agente AI por defecto de n8n, puedes usar tus propios agentes configurados en el CRM.
                  </p>
                  
                  <div className="space-y-4">
                    <div className="bg-white p-3 rounded border">
                      <p className="font-medium text-gray-800 text-sm mb-2">Paso 1: Obtener el ID de tu Agente</p>
                      <p className="text-xs text-gray-600 mb-2">Primero, lista tus agentes para obtener el ID:</p>
                      <code className="bg-gray-100 px-2 py-1 rounded text-xs block">GET {getBaseUrl()}/api/agents</code>
                      <p className="text-xs text-gray-500 mt-2">Respuesta: Lista de agentes con sus IDs</p>
                    </div>
                    
                    <div className="bg-white p-3 rounded border">
                      <p className="font-medium text-gray-800 text-sm mb-2">Paso 2: Enviar Mensaje al Agente</p>
                      <p className="text-xs text-gray-600 mb-2">Usa el endpoint de chat con el ID de tu agente:</p>
                      <code className="bg-gray-100 px-2 py-1 rounded text-xs block whitespace-pre-wrap">{`POST ${getBaseUrl()}/api/agents/{AGENT_ID}/chat

Body:
{
  "message": "Tu mensaje o instrucción",
  "empresa_id": "ID de empresa (opcional)",
  "session_id": "ID de sesión para conversación continua"
}`}</code>
                    </div>

                    <div className="bg-white p-3 rounded border">
                      <p className="font-medium text-gray-800 text-sm mb-2">Paso 3: Ejecutar Acciones Automáticas</p>
                      <p className="text-xs text-gray-600 mb-2">Para que el agente ejecute acciones en el CRM:</p>
                      <code className="bg-gray-100 px-2 py-1 rounded text-xs block whitespace-pre-wrap">{`POST ${getBaseUrl()}/api/agents/{AGENT_ID}/execute

Body:
{
  "instruction": "Crea una tarea de seguimiento para mañana",
  "empresa_id": "ID de empresa",
  "execute_immediately": true
}`}</code>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <h4 className="font-medium text-orange-800 mb-2">Flujo de Aprobación de Acciones</h4>
                  <p className="text-sm text-orange-700 mb-3">
                    Si <code className="bg-orange-100 px-1 rounded">execute_immediately</code> es <code className="bg-orange-100 px-1 rounded">false</code>, 
                    las acciones quedan pendientes de aprobación:
                  </p>
                  <ol className="text-sm text-orange-700 space-y-2 list-decimal list-inside">
                    <li>Consulta acciones pendientes: <code className="bg-orange-100 px-1 rounded">GET /api/agents/{'{AGENT_ID}'}/actions?status=pending</code></li>
                    <li>Aprueba: <code className="bg-orange-100 px-1 rounded">POST /api/agents/{'{AGENT_ID}'}/actions/{'{ACTION_ID}'}/approve</code></li>
                    <li>O rechaza: <code className="bg-orange-100 px-1 rounded">POST /api/agents/{'{AGENT_ID}'}/actions/{'{ACTION_ID}'}/reject</code></li>
                  </ol>
                </div>

                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-medium text-green-800 mb-2">Webhook Bidireccional n8n</h4>
                  <p className="text-sm text-green-700 mb-2">
                    Para recibir eventos del CRM en n8n, configura el webhook:
                  </p>
                  <code className="bg-green-100 px-2 py-1 rounded text-xs block mb-2">{getBaseUrl()}/api/n8n/webhook</code>
                  <p className="text-xs text-green-600">
                    Eventos disponibles: <code>task_created</code>, <code>deal_updated</code>, <code>empresa_stage_changed</code>, <code>action_completed</code>
                  </p>
                </div>
              </TabsContent>

              {/* Endpoints Tab */}
              <TabsContent value="endpoints" className="space-y-2 mt-4">
                {CRM_API_ENDPOINTS.map((cat) => (
                  <div key={cat.category} className="border rounded-lg overflow-hidden">
                    <button 
                      onClick={() => toggleCategory(cat.category)}
                      className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <span className="font-medium text-gray-800">{cat.category}</span>
                      {expandedCategories[cat.category] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </button>
                    {expandedCategories[cat.category] && (
                      <div className="divide-y">
                        {cat.endpoints.map((ep, idx) => (
                          <div key={idx} className="p-3 text-sm">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={`text-xs ${
                                ep.method === 'GET' ? 'bg-blue-100 text-blue-700' :
                                ep.method === 'POST' ? 'bg-green-100 text-green-700' :
                                ep.method === 'PUT' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {ep.method}
                              </Badge>
                              <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">{ep.path}</code>
                              <button 
                                onClick={() => copyToClipboard(`${getBaseUrl()}${ep.path}`, `endpoint-${idx}`)}
                                className="p-1 hover:bg-gray-200 rounded"
                              >
                                {copiedText === `endpoint-${idx}` ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                              </button>
                            </div>
                            <p className="text-gray-600 text-xs">{ep.description}</p>
                            {ep.params && <p className="text-gray-500 text-xs mt-1">Params: {ep.params}</p>}
                            {ep.body && <p className="text-gray-500 text-xs mt-1 font-mono">Body: {ep.body}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </TabsContent>

              {/* Examples Tab */}
              <TabsContent value="examples" className="space-y-4 mt-4">
                <div className="space-y-4">
                  {/* cURL Example */}
                  <div className="p-4 bg-gray-900 rounded-lg text-green-400 font-mono text-xs overflow-x-auto">
                    <p className="text-gray-500 mb-2"># Listar empresas</p>
                    <pre className="whitespace-pre-wrap">{`curl -X GET "${getBaseUrl()}/api/empresas" \\
  -H "X-API-Key: ${generatedKey?.key || 'TU_API_KEY'}"`}</pre>
                  </div>

                  <div className="p-4 bg-gray-900 rounded-lg text-green-400 font-mono text-xs overflow-x-auto">
                    <p className="text-gray-500 mb-2"># Crear una empresa</p>
                    <pre className="whitespace-pre-wrap">{`curl -X POST "${getBaseUrl()}/api/empresas" \\
  -H "X-API-Key: ${generatedKey?.key || 'TU_API_KEY'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Nueva Empresa",
    "email": "contacto@empresa.com",
    "phone": "(787) 555-1234",
    "stage": "Lead",
    "category": "Empresa"
  }'`}</pre>
                  </div>

                  <div className="p-4 bg-gray-900 rounded-lg text-green-400 font-mono text-xs overflow-x-auto">
                    <p className="text-gray-500 mb-2"># Crear una tarea</p>
                    <pre className="whitespace-pre-wrap">{`curl -X POST "${getBaseUrl()}/api/tasks" \\
  -H "X-API-Key: ${generatedKey?.key || 'TU_API_KEY'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Llamar cliente",
    "description": "Seguimiento de propuesta",
    "due_date": "2025-12-20T10:00:00",
    "priority": "alta",
    "empresa_id": "ID_DE_EMPRESA"
  }'`}</pre>
                  </div>

                  <div className="p-4 bg-gray-900 rounded-lg text-green-400 font-mono text-xs overflow-x-auto">
                    <p className="text-gray-500 mb-2"># Enviar email con Gmail</p>
                    <pre className="whitespace-pre-wrap">{`curl -X POST "${getBaseUrl()}/api/google/gmail/send" \\
  -H "X-API-Key: ${generatedKey?.key || 'TU_API_KEY'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "to": "cliente@example.com",
    "subject": "Propuesta de servicios",
    "body": "Hola, adjunto nuestra propuesta...",
    "empresa_id": "ID_DE_EMPRESA"
  }'`}</pre>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          <DialogFooter className="mt-4 pt-4 border-t">
            <Button onClick={() => setShowInstructionsModal(false)} className="bg-[#D4A843] hover:bg-[#c49a3a]">
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ApiKeysTab;
